import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AdminData } from 'src/app/models/AdminData';
import jwt_decode from 'jwt-decode';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment.prod';
import { Observable } from 'rxjs/internal/Observable';
import { tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AdminService {

  user = new AdminData();
  public loading = false;
  public error: any;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.parseToken();

    const token = localStorage.getItem('@administrator');
    if (token) {
      this.getMyInfo().subscribe({
        next: (me: any) => {
          const meData = me?.data ?? me;
          this.user.setData(meData);
        },
        error: () => {}
      });
    }
  }

  login(value: any) {
    return new Promise(serve => {
      this.loading = true;
      this.http.post(environment.apiUrl + 'admin/login', value, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        withCredentials: true
      }).subscribe((response: any) => {
        this.loading = false;
        this.error = {};

        const payload = response?.data ?? response;
        const accessToken: string | undefined = payload?.access_token;
        const refreshToken: string | undefined = payload?.refresh_token;

        if (accessToken) {
          this.setTokens(accessToken);

          if (refreshToken) {
            localStorage.setItem('@refresh_token', refreshToken);
          }

          this.getMyInfo().subscribe({
            next: (me: any) => {
              const meData = me?.data ?? me;
              this.user.setData(meData);
              serve(true);
            },
            error: () => serve(true)
          });
          return;
        }

        serve(false);
      }, () => {
        this.loading = false;
        serve(false);
      });
    });
  }

  setTokens(accessToken: string) {
    if (accessToken) {
      localStorage.setItem('@administrator', accessToken);
      this.parseToken(accessToken);
    }
  }

  parseToken(token: any = '') {
    try {
      if (!token) {
        token = localStorage.getItem('@administrator');
      }
      if (token) {
        this.user.setData(jwt_decode(token));
      }
    } catch {}
  }

  isAdmin() {
    return this.isLogged();
  }

  /** FIXED: correct token key check */
  public isLogged(): boolean {
    if ((this.user as any)?.id) return true;
    const token = localStorage.getItem('@administrator');
    return !!token;
  }

  role() {
    return this.user.role;
  }

  getPermissions(): string[] {
    return (this.user as any)?.permission || [];
  }

  getMenuPermissions(): any[] {
    return (this.user as any)?.menu_permissions || [];
  }

  hasPermission(permission: string): boolean {
    if (!this.user) return false;
    if (this.user.role === 'admin') return true;
    if (this.user.permission?.includes(permission)) return true;

    if (this.user.menu_permissions?.length) {
      const [, action] = permission.split('_');
      return this.user.menu_permissions.some((p: any) => {
        if (!p.menu) return false;
        switch (action) {
          case 'view': return p.can_view;
          case 'create': return p.can_create;
          case 'edit': return p.can_edit;
          case 'delete': return p.can_delete;
          default: return false;
        }
      });
    }

    return false;
  }

  logout(silent: boolean = false) {
    localStorage.removeItem('@administrator');
    localStorage.removeItem('@refresh_token');
    this.user.setData({});

    if (!silent) {
      this.http.post(`${environment.apiUrl}admin/logout`, {}, { withCredentials: true }).subscribe();
    }

    this.router.navigate(['login']);
  }

  refreshToken() {
    return this.http.post<any>(environment.apiUrl + 'admin/refresh', {}, { withCredentials: true }).pipe(
      tap((response: any) => {
        const payload = response?.data ?? response;
        const accessToken: string | undefined = payload?.access_token;
        const refreshToken: string | undefined = payload?.refresh_token;

        if (accessToken) this.setTokens(accessToken);
        if (refreshToken) localStorage.setItem('@refresh_token', refreshToken);
      })
    );
  }

  getAdmins() {
    return this.http.get(environment.apiUrl + 'admin/admins');
  }

  addAdmin(data: any): Observable<any> {
    return this.http.post(environment.apiUrl + 'admin/admins', data);
  }

  updateAdmin(data: any): Observable<any> {
    return this.http.post(environment.apiUrl + `admin/admins/${data.id}`, data);
  }

  getMyInfo() {
    return this.http.get(environment.apiUrl + 'admin/me');
  }

  detete(id: number) {
    return this.http.delete(environment.apiUrl + `admin/admins/${id}`);
  }
}
